/**
 * DatatableFullscreenGrid — the full data-grid experience shown when a datatable
 * block is expanded to fullscreen. Built on @tanstack/react-table.
 *
 * Capabilities (fullscreen only — the inline chat table stays lightweight):
 *   • choose which columns are visible
 *   • drag column headers to reorder
 *   • drag the right edge of a header to resize
 *   • click a header to sort; shift-click to add a secondary/tertiary sort
 *   • a global search box plus a per-column filter row
 *   • copy the current view (respecting visibility/order/filters/sort) to the
 *     clipboard as Markdown / CSV, or download as XLSX
 */
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  type ColumnDef as RTColumnDef,
  type SortingState,
  type VisibilityState,
  type ColumnOrderState,
  type ColumnSizingState,
  type ColumnFiltersState,
} from '@tanstack/react-table'
import { Search, Columns3, Filter, X, ArrowUp, ArrowDown, RotateCcw, Check, GripVertical } from 'lucide-react'
import { cn } from '../../lib/utils'
import {
  DropdownMenu,
  DropdownMenuTrigger,
  StyledDropdownMenuContent,
  StyledDropdownMenuItem,
} from '../ui/StyledDropdown'
import { TableExportDropdown } from './TableExportDropdown'
import { type ColumnDef, formatCell, colAlign, compareCellValues } from './datatable-cell'

export interface DatatableFullscreenGridProps {
  columns: ColumnDef[]
  rows: Record<string, unknown>[]
  filename: string
}

export function DatatableFullscreenGrid({ columns, rows, filename }: DatatableFullscreenGridProps) {
  const { t } = useTranslation()

  const colByKey = React.useMemo(() => new Map(columns.map((c) => [c.key, c])), [columns])

  // Table state (controlled so the toolbar and copy can read the live view).
  const [sorting, setSorting] = React.useState<SortingState>([])
  const [columnVisibility, setColumnVisibility] = React.useState<VisibilityState>({})
  const [columnOrder, setColumnOrder] = React.useState<ColumnOrderState>(() => columns.map((c) => c.key))
  const [columnSizing, setColumnSizing] = React.useState<ColumnSizingState>({})
  const [globalFilter, setGlobalFilter] = React.useState('')
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([])
  const [showFilters, setShowFilters] = React.useState(false)

  // Native drag-reorder bookkeeping.
  const [dragKey, setDragKey] = React.useState<string | null>(null)

  const rtColumns = React.useMemo<RTColumnDef<Record<string, unknown>>[]>(
    () =>
      columns.map((c) => ({
        id: c.key,
        accessorFn: (row) => row[c.key],
        header: c.label,
        cell: (info) => formatCell(info.getValue(), c.type),
        enableSorting: true,
        enableResizing: true,
        filterFn: 'includesString',
        sortingFn: (a, b, colId) => compareCellValues(a.getValue(colId), b.getValue(colId)),
        minSize: 60,
        size: 180,
      })),
    [columns],
  )

  const table = useReactTable({
    data: rows,
    columns: rtColumns,
    state: { sorting, columnVisibility, columnOrder, columnSizing, globalFilter, columnFilters },
    onSortingChange: setSorting,
    onColumnVisibilityChange: setColumnVisibility,
    onColumnOrderChange: setColumnOrder,
    onColumnSizingChange: setColumnSizing,
    onGlobalFilterChange: setGlobalFilter,
    onColumnFiltersChange: setColumnFilters,
    globalFilterFn: 'includesString',
    enableMultiSort: true,
    columnResizeMode: 'onChange',
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  })

  const moveColumn = React.useCallback((from: string, to: string) => {
    if (from === to) return
    setColumnOrder((prev) => {
      const order = prev.length ? [...prev] : columns.map((c) => c.key)
      const fromIdx = order.indexOf(from)
      const toIdx = order.indexOf(to)
      if (fromIdx < 0 || toIdx < 0) return prev
      order.splice(fromIdx, 1)
      order.splice(toIdx, 0, from)
      return order
    })
  }, [columns])

  const hasActiveView =
    sorting.length > 0 ||
    columnFilters.length > 0 ||
    globalFilter.trim() !== '' ||
    Object.values(columnVisibility).some((v) => v === false) ||
    columnOrder.join() !== columns.map((c) => c.key).join() ||
    Object.keys(columnSizing).length > 0

  const resetView = React.useCallback(() => {
    setSorting([])
    setColumnVisibility({})
    setColumnOrder(columns.map((c) => c.key))
    setColumnSizing({})
    setGlobalFilter('')
    setColumnFilters([])
    setShowFilters(false)
  }, [columns])

  // Copy/export reflects exactly what's visible now: visible+ordered columns and
  // the filtered+sorted rows.
  const exportColumns = table.getVisibleLeafColumns().map((col) => {
    const src = colByKey.get(col.id)
    return { key: col.id, label: src?.label ?? col.id, type: src?.type }
  })
  const exportRows = table.getRowModel().rows.map((r) => r.original)

  const visibleLeaf = table.getVisibleLeafColumns()

  return (
    <div className="bg-background shadow-minimal rounded-[12px] overflow-hidden">
      {/* Toolbar */}
      <div className="flex items-center gap-2 border-b border-border/60 px-3 py-2">
        <div className="relative flex-1 max-w-xs">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground/60" />
          <input
            value={globalFilter}
            onChange={(e) => setGlobalFilter(e.target.value)}
            placeholder={t('table.searchPlaceholder', { defaultValue: 'Search…' })}
            className="h-8 w-full rounded-[8px] border border-border/60 bg-foreground/[0.02] pl-8 pr-7 text-[13px] text-foreground outline-none placeholder:text-muted-foreground/50 focus:border-foreground/25"
          />
          {globalFilter && (
            <button
              type="button"
              onClick={() => setGlobalFilter('')}
              aria-label={t('common.clear', { defaultValue: 'Clear' })}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground/50 hover:text-foreground"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>

        <div className="ml-auto flex items-center gap-1.5">
          {/* Per-column filter row toggle */}
          <button
            type="button"
            onClick={() => setShowFilters((v) => !v)}
            className={cn(
              'inline-flex h-8 items-center gap-1.5 rounded-[8px] border px-2.5 text-[12.5px] font-medium transition-colors',
              showFilters || columnFilters.length > 0
                ? 'border-accent/30 bg-accent/[0.08] text-accent'
                : 'border-border/60 text-muted-foreground hover:text-foreground hover:bg-foreground/[0.04]',
            )}
            title={t('table.columnFilters', { defaultValue: 'Column filters' })}
          >
            <Filter className="h-3.5 w-3.5" />
            {t('table.filters', { defaultValue: 'Filters' })}
          </button>

          {/* Column visibility picker */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="inline-flex h-8 items-center gap-1.5 rounded-[8px] border border-border/60 px-2.5 text-[12.5px] font-medium text-muted-foreground transition-colors hover:text-foreground hover:bg-foreground/[0.04]"
                title={t('table.columns', { defaultValue: 'Columns' })}
              >
                <Columns3 className="h-3.5 w-3.5" />
                {t('table.columns', { defaultValue: 'Columns' })}
              </button>
            </DropdownMenuTrigger>
            <StyledDropdownMenuContent align="end" className="max-h-[60vh] overflow-y-auto min-w-44">
              {table.getAllLeafColumns().map((col) => {
                const label = colByKey.get(col.id)?.label ?? col.id
                const isVisible = col.getIsVisible()
                // Never let the user hide the last visible column.
                const isLast = isVisible && visibleLeaf.length === 1
                return (
                  <StyledDropdownMenuItem
                    key={col.id}
                    onSelect={(e) => { e.preventDefault(); if (!isLast) col.toggleVisibility() }}
                    className={cn(isLast && 'opacity-60')}
                  >
                    <span className="flex-1">{label}</span>
                    {isVisible && <Check className="h-3.5 w-3.5 text-accent" />}
                  </StyledDropdownMenuItem>
                )
              })}
            </StyledDropdownMenuContent>
          </DropdownMenu>

          {/* Copy / export — reflects the current view */}
          <TableExportDropdown columns={exportColumns} rows={exportRows} filename={filename} />

          {hasActiveView && (
            <button
              type="button"
              onClick={resetView}
              className="inline-flex h-8 items-center gap-1.5 rounded-[8px] px-2 text-[12.5px] font-medium text-muted-foreground transition-colors hover:text-foreground hover:bg-foreground/[0.04]"
              title={t('common.reset', { defaultValue: 'Reset' })}
            >
              <RotateCcw className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>

      {/* Grid */}
      <div className="overflow-auto max-h-[calc(100vh-260px)]">
        <table className="text-[13px]" style={{ width: table.getCenterTotalSize() }}>
          <thead className="sticky top-0 z-[1] bg-foreground/[0.02] backdrop-blur">
            {table.getHeaderGroups().map((hg) => (
              <tr key={hg.id} className="border-b border-foreground/[0.08]">
                {hg.headers.map((header) => {
                  const col = header.column
                  const src = colByKey.get(col.id)
                  const sortDir = col.getIsSorted()
                  const sortIndex = col.getSortIndex()
                  const isMultiSort = sorting.length > 1
                  return (
                    <th
                      key={header.id}
                      style={{ width: header.getSize() }}
                      draggable
                      onDragStart={() => setDragKey(col.id)}
                      onDragOver={(e) => { e.preventDefault() }}
                      onDrop={(e) => { e.preventDefault(); if (dragKey) moveColumn(dragKey, col.id); setDragKey(null) }}
                      onDragEnd={() => setDragKey(null)}
                      className={cn(
                        'group/th relative select-none px-3 py-2 text-[12px] font-medium text-muted-foreground whitespace-nowrap',
                        colAlign(src?.type, src?.align),
                        dragKey === col.id && 'opacity-40',
                      )}
                    >
                      <span
                        onClick={col.getToggleSortingHandler()}
                        className={cn(
                          'inline-flex items-center gap-1 cursor-pointer hover:text-foreground transition-colors',
                          (src?.type === 'number' || src?.type === 'currency' || src?.type === 'percent') && 'flex-row-reverse',
                        )}
                        title={t('table.clickToSort', { defaultValue: 'Click to sort · Shift-click to add' })}
                      >
                        <GripVertical className="h-3 w-3 shrink-0 cursor-grab text-muted-foreground/25 opacity-0 group-hover/th:opacity-100" />
                        <span className="truncate">{src?.label ?? col.id}</span>
                        {sortDir === 'asc' && <ArrowUp className="h-3 w-3 shrink-0 text-accent" />}
                        {sortDir === 'desc' && <ArrowDown className="h-3 w-3 shrink-0 text-accent" />}
                        {sortDir && isMultiSort && (
                          <span className="text-[9px] font-semibold text-accent/70 tabular-nums">{sortIndex + 1}</span>
                        )}
                      </span>

                      {/* Resize handle */}
                      <span
                        onMouseDown={header.getResizeHandler()}
                        onTouchStart={header.getResizeHandler()}
                        onClick={(e) => e.stopPropagation()}
                        className={cn(
                          'absolute right-0 top-0 h-full w-1.5 cursor-col-resize touch-none select-none',
                          'opacity-0 group-hover/th:opacity-100',
                          col.getIsResizing() && 'opacity-100',
                        )}
                      >
                        <span className={cn('absolute right-0 top-1 bottom-1 w-px', col.getIsResizing() ? 'bg-accent' : 'bg-foreground/15')} />
                      </span>
                    </th>
                  )
                })}
              </tr>
            ))}

            {/* Per-column filter row */}
            {showFilters && table.getHeaderGroups().map((hg) => (
              <tr key={`f-${hg.id}`} className="border-b border-foreground/[0.06]">
                {hg.headers.map((header) => (
                  <th key={header.id} style={{ width: header.getSize() }} className="px-2 py-1.5">
                    <input
                      value={(header.column.getFilterValue() as string) ?? ''}
                      onChange={(e) => header.column.setFilterValue(e.target.value || undefined)}
                      placeholder={t('table.filterColumn', { defaultValue: 'Filter…' })}
                      className="h-6 w-full rounded-[6px] border border-border/60 bg-foreground/[0.02] px-1.5 text-[11.5px] font-normal text-foreground outline-none placeholder:text-muted-foreground/40 focus:border-foreground/25"
                    />
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.length ? table.getRowModel().rows.map((row) => (
              <tr key={row.id} className="border-b border-foreground/[0.03] last:border-0 hover:bg-foreground/[0.015] transition-colors">
                {row.getVisibleCells().map((cell) => {
                  const src = colByKey.get(cell.column.id)
                  return (
                    <td
                      key={cell.id}
                      style={{ width: cell.column.getSize() }}
                      className={cn('px-3 py-2 whitespace-nowrap overflow-hidden text-ellipsis', colAlign(src?.type, src?.align))}
                    >
                      {formatCell(cell.getValue(), src?.type)}
                    </td>
                  )
                })}
              </tr>
            )) : (
              <tr>
                <td colSpan={visibleLeaf.length} className="py-8 text-center text-muted-foreground text-[13px]">
                  {t('table.noMatchingRows', { defaultValue: 'No matching rows' })}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
